import React from "react";
import { useTranslation } from "react-i18next";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import Badge from "@surgia/ui/components/badge";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";
import {
  TableColumn,
  TenantsTableProps,
  getStatusVariant,
} from "@surgia/ui/containers/table/constants";
import { BadgeVariant } from "@surgia/ui/components/badge/constants";
import { useNavigate } from "react-router-dom";
const TenantsTable: React.FC<TenantsTableProps> = ({
  data,
  onViewDetails,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  onActivate,
  dataType = "tenants",
  sortState,
  onSort,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Business-specific column configuration
  const columns: TableColumn[] = [
    {
      key: "id",
      translationKey: "shared.table.id",
      sortable: true,
      sortKey: "id",
      render: (value: any, tenant: any) =>
        tenant.status === "active" ? (
          <div className="relative group">
            <button
              onClick={() => onViewDetails?.(tenant.id, navigate)}
              className="cursor-pointer text-purple-700 hover:text-purple-700/80 transition-colors"
            >
              {value}
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
              {`View ${value}`}
            </div>
          </div>
        ) : (
          <span>{value}</span>
        ),
    },
    {
      key: "tenantName",
      translationKey: "shared.table.tenantName",
      sortable: true,
      sortType: "string",
      sortKey: "name",
      render: (value: string) => (
        <TruncatedTooltip content={value} emphasized={true} as="span" />
      ),
    },
    {
      key: "type",
      translationKey: "shared.table.type",
      sortable: true,
      sortType: "string",
      sortKey: "type",
      render: (value: BadgeVariant) =>
        value ? (
          <Badge variant={value}>{t("shared.entityType." + value)}</Badge>
        ) : (
          <p className="text-gray-500 text-center">_</p>
        ),
    },
    {
      key: "city",
      translationKey: "shared.table.city",
      sortable: true,
      align: "left",
      sortKey: "city.name",
    },
    {
      key: "numberOfBranches",
      translationKey: "shared.table.numberOfBranches",
      sortable: false,
      sortType: "number",
      show: dataType == "tenants",
    },
    {
      key: "status",
      translationKey: "shared.table.status",
      sortable: false,
      align: "left",
      sortType: "string",
      render: (value: string) => (
        <Badge variant={getStatusVariant(value)}>
          {value === "active"
            ? t("shared.table.active")
            : t("shared.table.pending")}
        </Badge>
      ),
    },
    {
      key: "discount",
      translationKey: "shared.table.discountPercent",
      sortable: true,
      align: "left",
      sortType: "number",
      sortKey: "discountPercentage",
      render: (value: string) =>
        value ? <Badge variant="percentage">{value}%</Badge> : "-",
      show: dataType == "tenants",
    },
    {
      key: "contactName",
      translationKey: "shared.table.contactName",
      sortable: true,
      align: "left",
      sortType: "string",
      sortKey: "contactPersonFullName",
      render: (value: string) => (
        <TruncatedTooltip content={value} emphasized={true} as="span" />
      ),
    },
    {
      key: "contactDetails",
      translationKey: "shared.table.contactDetails",
      sortable: true,
      align: "left",
      sortType: "string",
      sortKey: "contactPersonEmail",
      render: (value: { email: string; phone: string }) => (
        <div className="text-gray-500">
          <TruncatedTooltip
            content={value.email}
            textClassName="text-[14px] text-[#181D27]"
          />
          <TruncatedTooltip content={value.phone} />
        </div>
      ),
    },
    {
      key: "dateCreated",
      translationKey: "shared.table.dateCreated",
      sortable: true,
      sortType: "date",
      sortKey: "createdAt",
      render: (value: string) => <span className="text-gray-500">{value}</span>,
    },
    {
      key: "actions",
      translationKey: "",
      sortable: false,
      render: (_: any, tenant: any) => {
        if (tenant.status === "active") {
          return null;
        }
        return (
          <span
            className="text-primary cursor-pointer text-success-700 font-[500]"
            onClick={() => onActivate?.(tenant.id, tenant)}
          >
            {t("admin.activate")}
          </span>
        );
      },
    },
  ];

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
    />
  );
};

export default TenantsTable;
